import { useCallback } from 'react';
import { create } from 'zustand';
import { EN } from './en';

// ═══════════════════════════════════════════════
// 界面语言 (中文 / English)
//
// 以中文原文作为翻译 key: t('仪表盘') 在中文下原样返回, 英文下查 EN 字典,
// 查不到就回落中文。这样 6000 多行硬编码中文可以按页面逐步迁移,
// 没迁移到的地方只是"还是中文", 不会出现空白或 key 名。
//
// 占位符写成 {name}: t('共 {n} 条', { n: 3 })。
// ═══════════════════════════════════════════════

export type Lang = 'zh' | 'en';

const LANG_KEY = 'fracsight_lang';

const readLang = (): Lang => {
  try {
    return localStorage.getItem(LANG_KEY) === 'en' ? 'en' : 'zh';
  } catch {
    return 'zh';
  }
};

const applyDocumentLang = (lang: Lang) => {
  if (typeof document !== 'undefined') document.documentElement.lang = lang === 'en' ? 'en' : 'zh-CN';
};

type LangState = { lang: Lang; setLang: (lang: Lang) => void };

export const useLangStore = create<LangState>((set) => ({
  lang: readLang(),
  setLang: (lang) => {
    try {
      localStorage.setItem(LANG_KEY, lang);
    } catch {
      // 存不下也照样切换, 只是刷新后回到中文
    }
    applyDocumentLang(lang);
    set({ lang });
  },
}));

applyDocumentLang(useLangStore.getState().lang);

export type TranslateVars = Record<string, string | number>;

export function translate(lang: Lang, text: string, vars?: TranslateVars): string {
  const template = lang === 'en' ? EN[text] ?? text : text;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match);
}

/** 非组件场景 (请求参数、工具函数) 用的当前语言。 */
export const getLang = (): Lang => useLangStore.getState().lang;

/** 组件里用: const t = useT(); t('仪表盘')。语言切换时组件自动重渲染。 */
export function useT() {
  const lang = useLangStore((s) => s.lang);
  return useCallback((text: string, vars?: TranslateVars) => translate(lang, text, vars), [lang]);
}
